import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ContentProperty,
    type CreativeTypes,
    ElementUpdateTypes,
    GuideManager,
    MultiPageVideoModel,
    PreviewTypes,
    SpecificationExporter,
    type TextStyles,
    VideoWebSpecificationParser,
} from '@bynder-studio/render-core';
import { creativeModelFactory, CreativeModelFactoryConfig } from '@bynder-studio/render-web';
import useVirtualProperties from 'packages/hooks/useVirtualProperties';
import useArrowScrollBlocker from 'packages/hooks/shortcuts/useArrowScrollBlocker';
import useCopyPasteShortcuts from 'packages/hooks/shortcuts/useCopyPasteShortcuts';
import { catchResponseError } from 'packages/helpers/helpers';
import { customerIdSelector } from 'packages/store/user/user.selectors';
import shotsCalculateService from 'packages/services/shotsCalculate';
import { dataURItoArrayBuffer } from '~/helpers/helpers';
import { prepareDataForShotsCalculate } from '~/common/editor/editor.bloc';
import useEditorUndoRedo from '~/hooks/useEditorUndoRedo';
import { getUserFonts } from '~/store/user/user.actions';
import defaultFontFile from '../../../../src/assets/fonts/SourceSansPro-Regular.ttf';
import debounce from 'packages/helpers/debounce';

type Props = {
    template: any;
    fonts: any[];
    children: React.ReactNode;
    textStyles: TextStyles[];
    contentProperties: ContentProperty[];
    type: string;
    creativeType: CreativeTypes;
    creativeId: number;
};

const defaultFontBlob = dataURItoArrayBuffer(defaultFontFile);

export const EditorContext = createContext({});

export function EditorProvider({
    children,
    template,
    fonts,
    textStyles,
    contentProperties,
    type,
    creativeType,
    creativeId,
}: Props) {
    const [version, setVersion] = useState(0);
    const forceUpdate = useCallback(() => setVersion((v) => v + 1), []);
    const dispatch = useDispatch();
    const data = useRef<any>({
        isPublishing: false,
        loading: true,
        creativeModel: undefined,
        assetLoader: undefined,
        creativeModelFactory: undefined,
        validationManager: undefined,
        undoRedoManager: undefined,
        manipulationRenderer: undefined,
        guideManager: undefined,
        type,
        creativeType,
    });
    const {
        exportIntegrations,
        toggleExportIntegration,
        changeExportValue,
        approvalEnabled,
        setApprovalEnabled,
        reviewers,
        addReviewer,
        removeReviewer,
        exportConfigurationIds,
        setExportConfigurationIds,
        globalsTab,
        setGlobalsTab,
        modalActive,
        setModalActive,
    } = useVirtualProperties({
        integrations: template.exportIntegrations || [],
        approvalEnabled: template.approvalEnabled || false,
        reviewers: template.reviewers || [],
        exportConfigurationIds: template.exportConfigurationIds ?? [],
    });
    const { canUndo } = useEditorUndoRedo(data.current.creativeModel);
    useArrowScrollBlocker();
    useCopyPasteShortcuts({
        creativeModel: data.current.creativeModel,
        manipulationRenderer: data.current.manipulationRenderer,
        creativeId,
        type: creativeType,
    });
    const customerId = useSelector(customerIdSelector);

    const fontLoadErrorRetry = useCallback(() => {
        dispatch(getUserFonts(customerId));
    }, []);

    const calculateShots = useCallback(() => {
        if (!data.current.creativeModel) {
            return;
        }

        const { creativeModel } = data.current;
        const { height, width } = creativeModel.getDimensions();

        const rawCreativePage = SpecificationExporter.exportPage(creativeModel.getCurrentModel(), creativeType);
        const { elements } = prepareDataForShotsCalculate(false, rawCreativePage.elements);
        const duration = creativeModel?.getPlaybackDuration()?.getDuration();

        shotsCalculateService
            .calculate({
                height,
                width,
                duration,
                elements,
                pageId: Number(creativeModel.getCurrentPageId()),
            })
            .then(({ json, status }) => {
                if (status === 200) {
                    const shots = VideoWebSpecificationParser.parseShots(json);
                    creativeModel.replaceShots(shots);
                    forceUpdate();
                }
            })
            .catch(catchResponseError);
    }, [creativeType, forceUpdate]);

    useEffect(() => {
        creativeModelFactory(creativeType, {
            template,
            defaultFontBlob,
            fontFamilies: fonts,
            textStyles,
            contentProperties,
            previewType: PreviewTypes.EDITOR,
            containerDocument: document,
        } as unknown as CreativeModelFactoryConfig).then((creativeModelFactory) => {
            const creativeModel = creativeModelFactory.getCreativeModel();
            data.current.isPublishing = false;
            data.current.creativeModelFactory = creativeModelFactory;
            data.current.creativeModel = creativeModel;
            data.current.assetLoader = creativeModelFactory.getAssetLoader();
            data.current.validationManager = creativeModelFactory.getValidationManager();
            data.current.guideManager = new GuideManager();
            data.current.loading = false;
            data.current.validationManager.validate();
            data.current.guideManager.setModel(creativeModel.getCurrentModel());
            forceUpdate();
        });

        return () => {
            if (!data.current) {
                return;
            }

            data.current.validationManager?.unsubscribe();
            data.current.manipulationRenderer?.canvasManipulation?.unsubscribeEvents();
        };
    }, [forceUpdate]);

    useEffect(() => {
        const { creativeModel } = data.current;

        if (!creativeModel) {
            return;
        }

        const elementsTreeUpdatedListener = debounce(() => {
            calculateShots();
        }, 10);

        if (creativeType === 'VIDEO') {
            creativeModel.on('elementsTreeUpdated', elementsTreeUpdatedListener);
            creativeModel.on('durationUpdated', elementsTreeUpdatedListener);
        }

        return () => {
            if (creativeType === 'VIDEO') {
                creativeModel.off('elementsTreeUpdated', elementsTreeUpdatedListener);
                creativeModel.off('durationUpdated', elementsTreeUpdatedListener);
            }
        };
    }, [data.current.creativeModel, calculateShots]);

    useEffect(() => {
        const { creativeModel } = data.current;

        if (!creativeModel || creativeType !== 'VIDEO') {
            return;
        }

        let shouldRunCalculation = false;
        const relevantElementUpdatedListener = debounce(() => {
            if (!shouldRunCalculation) {
                return;
            }

            shouldRunCalculation = false;
            calculateShots();
        }, 10);
        const handleRelevantElementUpdated = () => {
            shouldRunCalculation = true;
            relevantElementUpdatedListener();
        };

        const unsubscribe = creativeModel.onElementsPartialUpdate(
            ElementUpdateTypes.ACTIVE_DROPDOWN_ELEMENT_ID,
            handleRelevantElementUpdated,
        );

        return () => {
            unsubscribe();
        };
    }, [calculateShots, creativeType, data.current.creativeModel]);

    useEffect(() => {
        const { creativeModel, manipulationRenderer, guideManager, assetLoader } = data.current;
        const [mainContainer] = document.querySelectorAll('main');

        if (!creativeModel || !manipulationRenderer || !guideManager || !assetLoader) {
            return;
        }

        const handleDeselection = () => manipulationRenderer.selectElement(null);
        const handleElementSelection = ({ element }) => {
            return manipulationRenderer.selectElement(element.id);
        };
        const handleCurrentSizeChange = () => {
            if (manipulationRenderer) {
                manipulationRenderer.zoomToFit();

                if (creativeType === 'VIDEO') {
                    manipulationRenderer.pausePlayback();
                }
            }

            guideManager.setModel(creativeModel.getCurrentModel());
        };
        const markDirty = guideManager.markDirty.bind(guideManager);
        const setFrame = guideManager.setFrame.bind(guideManager);

        const handleDimensionResize = debounce(() => {
            if (document.fullscreenElement) {
                return;
            }

            if (manipulationRenderer) {
                manipulationRenderer.calculateScaleRatio(true);
                manipulationRenderer.redraw();
            }
        }, 10);

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                if ('getIsPlaying' in manipulationRenderer && manipulationRenderer.getIsPlaying()) {
                    manipulationRenderer.pausePlayback();
                }

                return;
            }

            manipulationRenderer.redraw();
        };

        const handleUndoRedoSnapshotRestore = ({ selectElementId }) => {
            guideManager.setModel(creativeModel.getCurrentModel());

            if (selectElementId) {
                manipulationRenderer.selectElement(selectElementId);
            } else {
                // After undo/redo, the model elements are recreated (new object references).
                // Refresh the selection to point to the new element instances.
                const selectedIds = manipulationRenderer.getSelectedElements().map((el) => el.id);

                if (selectedIds.length > 0) {
                    manipulationRenderer.selectElementsExact(selectedIds);
                }
            }

            if (
                manipulationRenderer.canvasManipulation?.compositor?.textEditorElement &&
                manipulationRenderer.canvasManipulation?.compositor?.textEditor
            ) {
                const textElement = creativeModel
                    .getCurrentModel()
                    .getElementById(manipulationRenderer.canvasManipulation.compositor.textEditorElement);

                if (textElement) {
                    manipulationRenderer.canvasManipulation.compositor.textEditor.unbindEvents();
                    manipulationRenderer.canvasManipulation.compositor.textEditor.setBText(textElement);
                    textElement.update({ isTextEditorActivated: true });
                }
            }

            forceUpdate();
        };

        const resizeObserver = new ResizeObserver(handleDimensionResize);
        resizeObserver.observe(mainContainer);
        document.addEventListener('visibilitychange', onVisibilityChange);
        creativeModel.on('dimensionUpdated', forceUpdate);
        creativeModel.on('elementRemoved', handleDeselection);
        creativeModel.on('elementCreated', handleElementSelection);
        creativeModel.on('dimensionUpdated', markDirty);
        creativeModel.on('elementRemoved', markDirty);
        creativeModel.on('elementCreated', markDirty);
        creativeModel.on('elementUpdated', markDirty);
        creativeModel.getEventEmitter().on('currentPageChange', handleCurrentSizeChange);
        creativeModel.getEventEmitter().on('undoRedoSnapshotRestore', handleUndoRedoSnapshotRestore);
        manipulationRenderer.eventEmitter.on('frameUpdate', setFrame);
        assetLoader.eventEmitter.on('font.load.error', fontLoadErrorRetry);

        return () => {
            resizeObserver.disconnect();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            creativeModel.off('dimensionUpdated', forceUpdate);
            creativeModel.off('elementRemoved', handleDeselection);
            creativeModel.off('elementCreated', handleElementSelection);
            creativeModel.off('dimensionUpdated', markDirty);
            creativeModel.off('elementRemoved', markDirty);
            creativeModel.off('elementCreated', markDirty);
            creativeModel.off('elementUpdated', markDirty);
            creativeModel.getEventEmitter().off('currentPageChange', handleCurrentSizeChange);
            creativeModel.getEventEmitter().off('undoRedoSnapshotRestore', handleUndoRedoSnapshotRestore);
            manipulationRenderer.eventEmitter.off('frameUpdate', setFrame);
            assetLoader.eventEmitter.off('font.load.error', fontLoadErrorRetry);
        };
    }, [data.current.manipulationRenderer, data.current.creativeModel, forceUpdate]);

    const initManipulationRenderer = useCallback(
        (canvasWrapper, currentFrame) => {
            const { creativeModelFactory, guideManager } = data.current;

            const manipulationRenderer = creativeModelFactory.getCreativeManipulationRenderer(canvasWrapper);
            manipulationRenderer.init().then(async () => {
                if (creativeType === 'VIDEO') {
                    await manipulationRenderer.setCurrentFrame(currentFrame);
                }

                data.current.manipulationRenderer = manipulationRenderer;
                forceUpdate();
            });

            if (guideManager) {
                guideManager.setHandler((xAxisSnapLines, yAxisSnapLines) => {
                    manipulationRenderer.setElementSnapLines(xAxisSnapLines, yAxisSnapLines);
                });
                manipulationRenderer.canvasManipulation.emitter.on('requestElementMove', (event) => {
                    guideManager.onElementChange('move', event);
                });
                manipulationRenderer.canvasManipulation.emitter.on('requestElementResize', (event) => {
                    guideManager.onElementChange('resize', event);
                });
                // manipulationRenderer.canvasManipulation.on('requestElementRotate', (event) =>
                //     guideManager.onElementChange('rotate', event),
                // );
            }
        },
        [forceUpdate],
    );

    const setIsPublishing = useCallback(
        (isPublishing) => {
            data.current.isPublishing = isPublishing;
            forceUpdate();
        },
        [forceUpdate],
    );

    useEffect(() => {
        const setFontFamilies = data.current?.creativeModelFactory?.setFontFamilies;

        if (setFontFamilies && fonts.length) {
            setFontFamilies(fonts);
        }
    }, [fonts]);

    const dimension = data.current.loading ? null : data.current.creativeModel.getDimensions();
    const width = dimension?.getWidth() ?? 0;
    const height = dimension?.getHeight() ?? 0;

    const value = useMemo(
        () => ({
            ...data.current,
            width,
            height,
            initManipulationRenderer,
            exportIntegrations,
            toggleExportIntegration,
            changeExportValue,
            approvalEnabled,
            setApprovalEnabled,
            reviewers,
            addReviewer,
            removeReviewer,
            exportConfigurationIds,
            setExportConfigurationIds,
            canUndo,
            globalsTab,
            setGlobalsTab,
            setIsPublishing,
            frameRate:
                data.current.creativeModel instanceof MultiPageVideoModel
                    ? data.current.creativeModel?.getPlaybackDuration()?.getFrameRate() || 25
                    : null,
            modalActive,
            setModalActive,
        }),
        [
            version,
            width,
            height,
            initManipulationRenderer,
            exportIntegrations,
            approvalEnabled,
            reviewers,
            exportConfigurationIds,
            canUndo,
            globalsTab,
            modalActive,
        ],
    );

    if (data.current.loading) {
        return null;
    }

    return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}
